import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, MaskedLines } from "../motion/Reveal";
import { services } from "../../content/services";

const layout = {
  "field-services": "md:col-span-2 lg:col-span-2 lg:row-span-2",
  "digital-infrastructure": "md:col-span-2 lg:col-span-2",
  "artificial-intelligence": "",
  "software-engineering": "",
  "hardware-software": "",
  "managed-services": "",
  cybersecurity: "md:col-span-2 lg:col-span-2",
  "drone-technology": "md:col-span-2 lg:col-span-2",
};
const withImage = new Set(services.map((s) => s.slug));

const Card = ({ s, i }) => {
  const reduce = useReducedMotion();
  const img = withImage.has(s.slug);
  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8, delay: (i % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className={`${layout[s.slug]} min-h-[260px]`}>
      <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
        className="tc-card group relative flex h-full flex-col justify-between overflow-hidden rounded-[22px] border border-white/10 bg-[#161311] p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-orange-500/50">
        {img && (
          <>
            <img src={s.image} alt={s.imageAlt} loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-75 brightness-[1.05] contrast-[1.08] grayscale-[45%] transition-[opacity,transform,filter] duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06] group-hover:opacity-90 group-hover:grayscale-0" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/70 to-[#0C0A09]/10 transition-opacity duration-700" />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0C0A09]/60 to-transparent" />
          </>
        )}
        <div className="relative flex items-start justify-between">
          <span className="font-mono text-xs text-orange-400">{s.index}</span>
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-stone-300 transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-orange-500 group-hover:text-black">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
        <div className="relative mt-12">
          <h3 className={`break-words font-display font-medium tracking-tight text-stone-50 ${s.slug === "field-services" ? "text-2xl sm:text-4xl" : "text-xl sm:text-2xl"}`}>{s.name}</h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-stone-200">{s.short}</p>
          <span className="mt-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-orange-300">
            <span className="h-px w-6 bg-orange-400 transition-[width] duration-500 group-hover:w-12" /> Explore service
          </span>
        </div>
        <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-[2px] origin-left scale-x-0 bg-gradient-to-r from-[#C2410C] to-[#FB923C] transition-transform duration-700 group-hover:scale-x-100" />
      </Link>
    </motion.div>
  );
};

export const ServicesBento = () => (
  <section id="services" data-testid="services-section" className="relative bg-[#0C0A09] py-24 text-stone-100 sm:py-32">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <Eyebrow>02 — Services & Solutions</Eyebrow>
          <h2 className="mt-8 max-w-3xl font-display text-3xl font-medium leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLines inView lines={["Eight disciplines.", "One accountable partner."]} />
          </h2>
        </div>
        <Link to="/services" data-testid="services-all-link" className="tc-link inline-flex items-center gap-2 text-sm text-stone-300 hover:text-orange-400">
          View all services <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-16 grid auto-rows-[minmax(260px,auto)] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {services.map((s, i) => <Card key={s.slug} s={s} i={i} />)}
        <Link to="/contact?type=site_survey" data-testid="services-survey-cta"
          className="group relative flex min-h-[260px] flex-col justify-between overflow-hidden rounded-[22px] bg-gradient-to-br from-[#C2410C] via-[#EA580C] to-[#F97316] p-7 text-black md:col-span-2 lg:col-span-2">
          <span className="font-mono text-xs uppercase tracking-[0.25em]">Not sure where to start?</span>
          <span className="flex items-end justify-between gap-6">
            <span className="font-display text-2xl font-medium leading-tight sm:text-3xl">Book a site survey and we'll map what you need.</span>
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-black text-orange-400 transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-5 w-5" />
            </span>
          </span>
        </Link>
      </div>
    </div>
  </section>
);
